<template>
  <div class="columns">
    <div class="column"></div>
    <div class="column is-narrow">
      <label
        class="checkbox"
        v-if="worker.repository_url"
      >
        <input
          type="checkbox"
          v-model="allVersions"
        />
        Display all versions
      </label>
      <ImportModal
        :worker-id="worker.id"
        class="is-pulled-right ml-3"
        v-on:created="fetchVersions"
      />
    </div>
  </div>

  <div
    v-if="error"
    class="notification is-warning"
  >
    {{ error }}
  </div>
  <template v-else>
    <Paginator
      :response="versionsPage"
      :loading="loading"
      v-slot="{ results }"
      v-model:page="page"
      singular="version"
      plural="versions"
    >
      <table class="table is-fullwidth is-hoverable">
        <thead>
          <tr>
            <th class="shrink">ID</th>
            <th>Version</th>
            <th>State</th>
            <th>Docker image</th>
            <th class="shrink">Created</th>
            <th class="shrink has-text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <Row
            v-for="version in results"
            :key="version.id"
            :version="version"
            :select-function="selectFunction"
            :only-select-available="onlySelectAvailable"
          />
        </tbody>
      </table>
    </Paginator>
  </template>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'

import type { WorkerVersionListParameters } from '@/api'
import Paginator from '@/components/Paginator.vue'
import { errorParser } from '@/helpers'
import { useWorkerStore } from '@/stores'
import type { PageNumberPagination } from '@/types'
import type { Worker, WorkerVersion } from '@/types/worker'

import ImportModal from './ImportModal.vue'
import Row from './Row.vue'

const { worker, selectFunction, onlySelectAvailable } = defineProps<{
  worker: Worker
  /**
   * A function called when a WorkerVersion is selected.
   * The button to select a worker version is only shown when this function is set.
   */
  selectFunction?: (version: WorkerVersion) => unknown | Promise<unknown>
  /**
   * When a `selectFunction` is defined, only a WorkerVersion with an `available` state will be selectable.
   */
  onlySelectAvailable?: boolean
}>()

const error = ref<string | null>(null)
const versionsPage = ref<PageNumberPagination<WorkerVersion>>()
const loading = ref(false)
const page = ref(1)
/**
 * If set to false, only display versions with a tag or from the master/main branch
 */
const allVersions = ref(false)

const workerStore = useWorkerStore()

async function fetchVersions() {
  versionsPage.value = undefined
  error.value = null
  loading.value = true
  let mode = 'complete' as WorkerVersionListParameters['mode']
  if (worker.repository_url) mode = allVersions.value ? 'complete' : 'simple'
  const payload: WorkerVersionListParameters = {
    page: page.value,
    mode,
  }
  try {
    versionsPage.value = await workerStore.listVersions(worker.id, payload)
  } catch (err) {
    error.value = errorParser(err)
  } finally {
    loading.value = false
  }
}

watch(page, fetchVersions)
watch(
  () => worker,
  (newValue: Worker, oldValue: Worker | undefined) => {
    if (oldValue?.id === newValue.id) return
    page.value = 1
    fetchVersions()
  },
  { immediate: true },
)
watch(allVersions, () => {
  page.value = 1
  fetchVersions()
})
</script>
